import {DataFetcherRequest} from '@aboutyou/data-fetcher';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useCallback, useMemo, useState} from 'react';

export interface NextPage<T> {
  page: number;
  token: T;
  request: InitialRequest<T> | NextRequest<T>;
  setNextToken: (nextToken: T | undefined | null) => void;
}

type InitialRequest<T> = () => DataFetcherRequest<{
  items: unknown[];
  nextState: T | undefined | null;
}>;
type NextRequest<T> = ({token}: {token: T}) => DataFetcherRequest<{
  items: unknown[];
  nextState: T | undefined | null;
}>;

export interface PageState<T> {
  page: number;
  token: T;
}

export const useTokenBasedStreamPages = <T extends Uint8Array | string>({
  uniqueKey,
  request,
  nextRequest,
}: {
  uniqueKey: string;
  request: () => {data?: {nextState?: T}};
  nextRequest?: (option: {token: T}) => {data?: {nextState?: T}};
}) => {
  const [pages, setNextPages] = useHistoryPageState<Array<PageState<T>>>(
    [{page: 1, token: undefined as unknown as T}],
    {
      uniqueKey: `product_stream_pages::${uniqueKey}`,
    },
  );

  const [nextTokenState, setNextTokenState] = useState<{
    nextToken: T | undefined | null;
  }>({nextToken: undefined});

  const setNextPageToken = useCallback(
    (page: number) => (nextToken: T | undefined | null) => {
      /**
       * do not set next token if the page is not the last one
       * this would cause the stream to set a wrong next token in cases where the user goes out
       * of the stream page (to ADP for example) and then comes back (history back)
       */
      if (page !== pages.length) {
        return;
      }

      if (nextToken === null) {
        setNextTokenState({nextToken: null});
        return;
      }

      setNextTokenState({nextToken});
    },
    [pages, setNextTokenState],
  );

  const mountNextPage = useCallback(() => {
    if (!nextTokenState.nextToken) {
      return;
    }

    if (pages.some(page => page.token === nextTokenState.nextToken)) {
      return;
    }

    const nextPage = pages.length + 1;

    setNextPages([
      ...pages,
      {
        page: nextPage,
        token: nextTokenState.nextToken,
      },
    ]);
    setNextTokenState({nextToken: undefined}); // we make the next token undefined to prevent multiple mounts of the same page when the block which mounts the next page is re-rendered
  }, [nextTokenState.nextToken, pages, setNextPages, setNextTokenState]);

  const memoizedPages = useMemo(() => {
    return pages.map(page => ({
      page: page.page,
      token: page.token,
      request:
        page.page === 1
          ? (request as InitialRequest<T>)
          : (nextRequest as NextRequest<T>),
      setNextToken: setNextPageToken(page.page),
    }));
  }, [pages, request, nextRequest, setNextPageToken]);

  return {
    pages: memoizedPages,
    mountNextPage,
    isNextPageReady: !!nextTokenState.nextToken,
    endOfStreamReached: nextTokenState.nextToken === null,
  };
};
